import { flatten } from 'lodash'
import { useInfiniteQuery, useQuery } from 'react-query'

import { CacheKeys } from '../../helpers/cacheKeys'
import { parseQueryParams } from '../../helpers/utils'
import { ApiVersion } from '../../models'
import { getNotes, getTimeline, NoteRecordType } from '../../models/note'

interface UseNotes {
  recordType: NoteRecordType
  recordId: string
  isTimeline?: boolean
  filterStatusHistoryOnly?: boolean
  tags?: string[] | null
  apiVersion?: ApiVersion
}

function getNotesQueryKey({ recordType, recordId, isTimeline, tags, apiVersion }: UseNotes) {
  return `${CacheKeys.NOTES}-${recordType}-${recordId}-${isTimeline}-${tags?.join(',')}-${apiVersion}`
}

export function useNotes(
  { recordType, recordId, isTimeline, filterStatusHistoryOnly, tags, apiVersion }: UseNotes,
  { enabled = true } = {}
) {
  const nextPageParamName = apiVersion === 'v1' ? 'page' : 'cursor'

  const {
    data: notes,
    fetchNextPage: fetchNotesNextPage,
    hasNextPage: hasNotesNextPage,
    isFetching: isNotesFetching,
    refetch: refetchNotes,
    isLoading: isNotesLoading,
  } = useInfiniteQuery(
    getNotesQueryKey({ recordType, recordId, isTimeline, tags }),
    ({ pageParam = undefined }) =>
      getNotes({ recordType, recordId, cursor: pageParam, tags, apiVersion, nextPageParamName }),
    {
      enabled: Boolean(!isTimeline && enabled),
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) =>
        lastPage.next ? parseQueryParams(lastPage.next)[nextPageParamName] : null,
    }
  )

  const {
    data: timeline,
    isFetching: isTimelineFetching,
    refetch: refetchTimeline,
    isLoading: isTimelineLoading,
  } = useQuery(
    getNotesQueryKey({ recordType, recordId, isTimeline, tags }),
    () => getTimeline({ recordType, recordId, filterStatusHistoryOnly, tags }),
    {
      enabled: Boolean(isTimeline && enabled),
      refetchOnWindowFocus: false,
    }
  )

  if (isTimeline) {
    return {
      data: timeline?.results,
      count: timeline?.totalCount,
      activityCount: timeline?.suspensionCount,
      refetch: refetchTimeline,
      isLoading: isTimelineLoading,
      isFetching: isTimelineFetching,
      hasNextPage: false,
      fetchNextPage: () => null,
    }
  }

  const data = flatten(notes?.pages.map((page) => page.results))
  return {
    data: data,
    count: notes?.pages[0].count || data?.length,
    refetch: refetchNotes,
    isLoading: isNotesLoading,
    isFetching: isNotesFetching,
    hasNextPage: hasNotesNextPage,
    fetchNextPage: fetchNotesNextPage,
  }
}
